const SELECTORS = {
  trigger: "[data-md-trigger]",
  closeBtn: "[data-md-close]",
  panel: "[data-md-panel]",
  mobileDrawer: "[data-md-mobile]",
  accItem: "[data-md-acc-item]",
  accBtn: "[data-md-acc-btn]",
  accBody: "[data-md-acc-body]",
};

export const MD_EVENTS = {
  OPENED: "material-drawer:opened",
  CLOSED: "material-drawer:closed",
};

class MaterialDrawer extends HTMLElement {
  #sidebarObserver = null;
  #scrollRafId = null;

  constructor() {
    super();
    this.onKeydown = this.onKeydown.bind(this);
    this.close = this.close.bind(this);
    this.onReflow = this.onReflow.bind(this);
    this.onScroll = this.onScroll.bind(this);
  }

  connectedCallback() {
    try {
      this.init();
    } catch (error) {
      console.error("[MaterialDrawer:init]", error);
    }
  }

  init() {
    this.#mirrorToMobile();
    this.#bindElements();
    this.#bindEvents();
    this.#observeSidebar();
  }

  #mirrorToMobile() {
    const source = this.querySelector("[data-md-panel-content]");
    const target = this.querySelector("[data-md-mobile-content]");
    if (!source || !target) return;
    target.innerHTML = source.innerHTML;
  }

  #bindElements() {
    this.trigger = this.querySelector(SELECTORS.trigger);
    this.closeBtn = this.querySelector(SELECTORS.closeBtn);
    this.panel = this.querySelector(SELECTORS.panel);
    this.mobileDrawer = this.querySelector(SELECTORS.mobileDrawer);
  }

  #bindEvents() {
    this.trigger?.addEventListener("click", () => this.#onTriggerClick());
    this.closeBtn?.addEventListener("click", this.close);
    this.querySelectorAll(SELECTORS.accBtn).forEach((btn) => {
      btn.addEventListener("click", (e) => this.#onAccBtnClick(e));
    });
  }

  #observeSidebar() {
    const sidebar = this.closest(".article-layout__sidebar");
    if (!sidebar) return;
    this.#sidebarObserver = new ResizeObserver(this.onReflow);
    this.#sidebarObserver.observe(sidebar);
  }

  // Keep the fixed panel glued to the sidebar column. The sidebar is a
  // fixed-width grid track, so a horizontal window resize moves its X position
  // (the centered container shifts) without changing its box size — the
  // ResizeObserver never fires for that case. A window resize listener covers
  // it. Below the desktop breakpoint the layout collapses to one column, so we
  // close instead of trying to reposition.
  onReflow() {
    if (!this.classList.contains("panel-open")) return;
    if (!window.matchMedia("(min-width: 768px)").matches) {
      this.close();
      return;
    }
    this.#repositionPanel();
  }

  #getSidebarRect() {
    const sidebar = this.closest(".article-layout__sidebar");
    return (sidebar ?? this).getBoundingClientRect();
  }

  #getSectionRect() {
    return this.closest("section")?.getBoundingClientRect() ?? null;
  }

  #getHeaderHeight() {
    return (
      parseFloat(
        getComputedStyle(document.documentElement).getPropertyValue(
          "--header-height"
        )
      ) || 0
    );
  }

  // Glue the fixed panel over the sidebar column, but clamp it to the article
  // section's box so it never bleeds into the sections below (comments, author
  // profile). Top: below the sticky header, or the section's top edge while it
  // still sits lower. Bottom: viewport floor, unless the section ends earlier
  // in view — then match its bottom edge. Both edges shift on scroll, hence the
  // scroll listener bound in openPanel().
  #repositionPanel() {
    const rect = this.#getSidebarRect();
    const sectionRect = this.#getSectionRect();
    const headerHeight = this.#getHeaderHeight();
    const top = sectionRect
      ? Math.max(headerHeight, sectionRect.top)
      : headerHeight;
    const bottom = sectionRect
      ? Math.max(0, window.innerHeight - sectionRect.bottom)
      : 0;
    Object.assign(this.panel.style, {
      top: top + "px",
      bottom: bottom + "px",
      left: rect.left + "px",
      width: rect.width + "px",
    });
  }

  onScroll() {
    if (this.#scrollRafId !== null) return;
    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      if (this.classList.contains("panel-open")) this.#repositionPanel();
    });
  }

  #onTriggerClick() {
    if (window.matchMedia("(min-width: 768px)").matches) {
      this.openPanel();
    } else {
      this.#toggleMobileDrawer();
    }
  }

  #toggleMobileDrawer() {
    if (this.mobileDrawer?.hasAttribute("hidden")) {
      this.mobileDrawer.removeAttribute("hidden");
    } else {
      this.mobileDrawer?.setAttribute("hidden", "");
    }
  }

  openPanel() {
    this.#repositionPanel();
    this.classList.add("panel-open");
    this.panel?.setAttribute("aria-hidden", "false");
    this.panel?.focus();
    document.addEventListener("keydown", this.onKeydown);
    window.addEventListener("resize", this.onReflow);
    window.addEventListener("scroll", this.onScroll, { passive: true });
    document.dispatchEvent(
      new CustomEvent(MD_EVENTS.OPENED, { detail: { source: this } })
    );
  }

  close() {
    this.classList.remove("panel-open");
    this.panel?.setAttribute("aria-hidden", "true");
    ["top", "bottom", "left", "width"].forEach((p) =>
      this.panel?.style.removeProperty(p)
    );
    document.removeEventListener("keydown", this.onKeydown);
    window.removeEventListener("resize", this.onReflow);
    window.removeEventListener("scroll", this.onScroll);
    if (this.#scrollRafId !== null) {
      cancelAnimationFrame(this.#scrollRafId);
      this.#scrollRafId = null;
    }
    this.trigger?.focus();
    document.dispatchEvent(
      new CustomEvent(MD_EVENTS.CLOSED, { detail: { source: this } })
    );
  }

  onKeydown(e) {
    if (e.key === "Escape") this.close();
  }

  #onAccBtnClick(e) {
    const item = e.currentTarget.closest(SELECTORS.accItem);
    if (!item) return;
    const body = item.querySelector(SELECTORS.accBody);
    if (!body) return;

    const isOpen = item.classList.contains("is-open");
    if (isOpen) {
      // The open body rests at height:auto (see below). Pin it to its pixel
      // height and force a reflow so the collapse transition has a start value.
      body.style.height = `${body.scrollHeight}px`;
      void body.offsetHeight;
      item.classList.remove("is-open");
      body.style.height = "0";
    } else {
      item.classList.add("is-open");
      body.style.height = `${body.scrollHeight}px`;
      // Release to auto once expanded so a nested <details> ("weitere Infos")
      // can grow the accordion body instead of being clipped by a fixed height.
      body.addEventListener(
        "transitionend",
        (ev) => {
          if (ev.propertyName === "height" && item.classList.contains("is-open")) {
            body.style.height = "auto";
          }
        },
        { once: true }
      );
    }
  }

  disconnectedCallback() {
    this.#cleanup();
  }

  #cleanup() {
    document.removeEventListener("keydown", this.onKeydown);
    window.removeEventListener("resize", this.onReflow);
    window.removeEventListener("scroll", this.onScroll);
    if (this.#scrollRafId !== null) {
      cancelAnimationFrame(this.#scrollRafId);
      this.#scrollRafId = null;
    }
    this.#sidebarObserver?.disconnect();
  }
}

if (!customElements.get("material-drawer")) {
  customElements.define("material-drawer", MaterialDrawer);
}
